import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { FEATURED, optimizeImg } from '../content/mediaLibrary';
import useSEO from '../hooks/useSEO';

const SERVICE_VIDEOS = FEATURED.serviceVideos;

const services = [
  {
    key: 'theCut',
    duration: 45,
    standard: '$45',
    pasqualino: '$65',
    signature: false,
    videoIndex: 0
  },
  {
    key: 'alaCasanova',
    duration: 60,
    standard: '$63',
    pasqualino: '$90',
    signature: true,
    videoIndex: 1
  },
  {
    key: 'theBeard',
    duration: 30,
    standard: '$34',
    pasqualino: '$45',
    signature: false,
    videoIndex: 2
  },
  {
    key: 'theHeadShave',
    duration: 30,
    standard: '$30',
    pasqualino: null,
    signature: false,
    videoIndex: 3
  },
  {
    key: 'bambini',
    duration: 30,
    standard: '$33.50',
    pasqualino: '$45',
    signature: false,
    videoIndex: 4
  }
];

const ServicesPage = () => {
  const { t } = useLanguage();
  useSEO({ title: t.seo.services.title, description: t.seo.services.description });
  const [activeService, setActiveService] = useState(1); // À la Casanova default

  return (
    <div className="min-h-screen pt-20" data-testid="services-page">
      {/* Hero Header — full-bleed cinematic video with signature tag */}
      <section
        className="relative w-full overflow-hidden bg-[#1A1A18] min-h-[60vh] lg:min-h-[70vh]"
        data-testid="services-hero"
      >
        {/* Background video */}
        <video
          src={FEATURED.heroVideo}
          poster={optimizeImg(FEATURED.heroPoster, 1600, 78)}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="services-hero-video"
        />

        {/* Left-side dark wash for text legibility + bottom vignette */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(100deg, rgba(26,26,24,0.78) 0%, rgba(26,26,24,0.50) 35%, rgba(26,26,24,0.15) 65%, rgba(26,26,24,0.55) 100%)',
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, rgba(26,26,24,0.55) 0%, transparent 25%, transparent 65%, rgba(26,26,24,0.85) 100%)',
          }}
        />

        {/* Top-left eyebrow + title */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-28 lg:pt-36 pb-36 lg:pb-44">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="deco-line" />
              <span className="eyebrow text-[#8A8780]">{t.nav.services}</span>
            </div>
            <h1
              className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-normal tracking-tight text-[#F5F0E8] leading-[0.98] mb-6"
              data-testid="services-title"
            >
              {t.services.title}
            </h1>
            <p
              className="font-body font-light text-base lg:text-lg text-[#F5F0E8]/75 max-w-xl"
              style={{ lineHeight: 1.85 }}
            >
              {t.services.subtitle}
            </p>
          </motion.div>
        </div>

        {/* Always-on signature service tag, bottom-center */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4 }}
          className="absolute bottom-8 lg:bottom-12 left-1/2 -translate-x-1/2 z-10 text-center px-4"
          data-testid="services-signature-tag"
        >
          <span className="eyebrow text-[#F5F0E8]/60 block mb-2">
            {t.services.signatureLabel}
          </span>
          <div className="w-12 h-[1px] bg-[#A14223] mx-auto mb-3" />
          <span className="font-display italic text-2xl lg:text-4xl text-[#F5F0E8] tracking-tight">
            {t.services.items.alaCasanova.name}
          </span>
        </motion.div>
      </section>

      {/* Interactive Services Section — desktop only */}
      <section className="hidden lg:grid lg:grid-cols-2 service-interactive">
        {/* Left Menu - Parchment */}
        <div className="bg-[#F5F0E8] p-8 lg:p-12 lg:sticky lg:top-20 lg:h-[calc(100vh-5rem)]">
          <div className="space-y-0">
            {services.map((service, index) => {
              const serviceData = t.services.items[service.key];
              return (
                <div
                  key={service.key}
                  className={`service-menu-item ${activeService === index ? 'active' : ''}`}
                  onMouseEnter={() => setActiveService(index)}
                  data-testid={`service-menu-${service.key}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`service-name font-display text-xl transition-colors ${activeService === index ? 'text-[#A14223]' : 'text-[#1A1A18]'}`}>
                      {serviceData.name}
                    </span>
                    {service.signature && (
                      <span className="badge-signature">{t.services.signature}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 font-body font-light text-sm text-[#8A8780]">
                    <span>{service.duration} {t.services.duration}</span>
                    <span>·</span>
                    <span className="font-medium text-[#1A1A18]">
                      {service.pasqualino ? `${service.standard} – ${service.pasqualino}` : service.standard}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          
          <p className="mt-8 eyebrow text-[#8A8780] leading-relaxed border-l-2 border-[#A14223] pl-4" data-testid="price-note">
            {t.services.priceNote}
          </p>
        </div>

        {/* Right Video + Details - Noir */}
        <div className="relative bg-[#1A1A18] min-h-[60vh] lg:min-h-screen">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeService}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="absolute inset-0"
            >
              <video
                src={SERVICE_VIDEOS[services[activeService].videoIndex]}
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-cover opacity-60"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A18] via-[#1A1A18]/50 to-transparent" />
            </motion.div>
          </AnimatePresence>
          
          {/* Service Details Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-12">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeService}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
              >
                <h2 className="font-display text-4xl lg:text-5xl text-[#F5F0E8] mb-4">
                  {t.services.items[services[activeService].key].name}
                </h2>
                <p className="font-body font-light text-sm text-white/70 max-w-lg mb-6" style={{ lineHeight: 1.85 }}>
                  {t.services.items[services[activeService].key].description}
                </p>
                <Link
                  to="/booking"
                  className="btn-cta inline-flex items-center gap-3"
                  data-testid={`book-${services[activeService].key}`}
                >
                  {t.services.bookThis}
                  <ArrowRight size={14} />
                </Link>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* Mobile Services List */}
      <section className="lg:hidden py-12 bg-[#F5F0E8]">
        <div className="px-6">
          {services.map((service) => {
            const serviceData = t.services.items[service.key];
            return (
              <div
                key={service.key}
                className={`py-6 border-b border-[#8A8780]/20 ${service.signature ? 'bg-[#A14223]/5 -mx-6 px-6 border-l-4 border-l-[#A14223]' : ''}`}
                data-testid={`service-mobile-${service.key}`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-display text-xl text-[#1A1A18]">{serviceData.name}</span>
                    {service.signature && <span className="badge-signature">{t.services.signature}</span>}
                  </div>
                  <span className="font-body font-medium text-[#1A1A18]">
                    {service.pasqualino ? `${service.standard}+` : service.standard}
                  </span>
                </div>
                <p className="font-body font-light text-sm text-[#8A8780] mb-3">{serviceData.description}</p>
                <div className="flex justify-between items-center">
                  <span className="eyebrow text-[#8A8780]">{service.duration} {t.services.duration}</span>
                  <Link to="/booking" className="eyebrow text-[#A14223] hover:text-[#8a3519]">
                    {t.services.bookThis} →
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Long-form SEO copy — Petite-Italie / Little Italy */}
      <section
        className="relative py-20 lg:py-32 bg-[#F5F0E8] texture-grain overflow-hidden"
        data-testid="services-copy-section"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="deco-line" />
              <span className="eyebrow text-[#8A8780]">
                {t.services.copy.eyebrow}
              </span>
            </div>
            <h2
              className="font-display text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-[#1A1A18] leading-[1.1] mb-10 lg:mb-12"
              data-testid="services-copy-title"
            >
              {t.services.copy.title}
            </h2>

            <div
              className="space-y-6 font-body font-light text-base lg:text-lg text-[#1A1A18]/80 max-w-3xl"
              style={{ lineHeight: 1.9 }}
            >
              {t.services.copy.paragraphs.map((p, i) => (
                <p key={i} data-testid={`services-copy-p-${i + 1}`}>
                  {p}
                </p>
              ))}
            </div>

            <div className="mt-12 lg:mt-16 flex flex-col sm:flex-row items-start sm:items-center gap-6 sm:gap-10">
              <Link
                to="/booking"
                className="btn-cta inline-flex items-center gap-3"
                data-testid="services-copy-cta"
              >
                {t.services.copy.cta}
                <ArrowRight size={14} />
              </Link>
              <address
                className="not-italic font-body text-sm text-[#1A1A18]/70 tracking-wide"
                data-testid="services-copy-address"
              >
                {t.services.copy.address}
              </address>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
